import type { ChangeEvent, KeyboardEvent } from 'react';

/**
 * A method validate the credit card number.
 *
 * @param cardNumber - The card number to validate
 */
export const luhnAlgorithmCheck = (cardNumber: string): boolean => {
  if (!cardNumber || !cardNumber.length) {
    return false;
  }

  /** Remove all whitespaces from the card number.  */
  const trimmedCardNumber = cardNumber.replace(/\s/g, '');

  /** 1. Remove last digit.  */
  const lastDigit = Number(trimmedCardNumber[trimmedCardNumber.length - 1]);

  /** 2. Reverse card number.  */
  const reverseCardNumber = trimmedCardNumber
    .slice(0, trimmedCardNumber.length - 1)
    .split('')
    .reverse()
    .map(Number);

  let sum = 0;

  /** 3. + 4. Multiply by 2 every digit on odd position. Subtract 9 if the digit > 9.  */
  for (let i = 0; i < reverseCardNumber.length; i += 2) {
    let doubledDigit = reverseCardNumber[i] * 2;
    if (doubledDigit > 9) {
      doubledDigit -= 9;
    }
    reverseCardNumber[i] = doubledDigit;
  }

  /** 5. Make the sum of obtained values from step 4.  */
  sum = reverseCardNumber.reduce((acc, currValue) => acc + currValue, 0);

  /** 6. Calculate modulo 10 of the sum from step 5 and the last digit. If it's 0, you have a valid card number :) */
  return (sum + lastDigit) % 10 === 0;
};

export const isDecimalNumber = (number: number) => number % 1 !== 0;

/** Function to mask the credit card number */
export const maskCreditCardNumber = (event: ChangeEvent<HTMLInputElement>): void => {
  const trimmedValue = event.target.value.replace(/\s+/g, '');
  const numbers: string[] = [];

  for (let i = 0; i < trimmedValue.length; i += 4) {
    const chunk = trimmedValue.slice(i, i + 4);
    numbers.push(chunk);
  }

  event.target.value = numbers.join(' ');
};

/**
 * Function to prevent entering non numerical inputs
 *
 * @param event - the input event
 */
export const preventNonNumericalInput = (event: KeyboardEvent<HTMLInputElement>): void => {
  const charCode = event.which ?? event.keyCode;
  const charStr = String.fromCharCode(charCode);
  const ALLOWED_KEY_CODES = [37, 38, 39, 40, 46, 17];

  if (
    !charStr.match(/^[0-9\b]+$/) &&
    !ALLOWED_KEY_CODES.includes(charCode) &&
    !(event.keyCode === 65 && event.ctrlKey)
  ) {
    event.preventDefault();
  }
};

/**
 * Function to prevent entering negative numbers
 *
 * @param event - the input event
 */
export const preventNegativeNumbers = (event: ChangeEvent<HTMLInputElement>) => {
  const inputValue = event.target.value ? Number(event.target.value) : 0;

  if (inputValue < 0) {
    const formattedValue = Math.abs(inputValue);
    event.target.value = isDecimalNumber(formattedValue)
      ? String(formattedValue)
      : `${formattedValue.toFixed(2)}`;
    return;
  }

  const inputValueStr = event.target.value;
  if (inputValueStr.includes('.') && inputValueStr.split('.')[1]?.length > 2) {
    event.target.value = inputValueStr.slice(0, inputValueStr.indexOf('.') + 3);
  }
};

/**
 * Method to allow only numbers in the input field.
 */
export const allowOnlyNumbers = (event: ChangeEvent<HTMLInputElement>) => {
  const inputValue = event.target.value;
  const numericValue = inputValue.replace(/\D/g, ''); /**  Remove non-numeric characters */

  event.target.value = numericValue; /** Update the input value with only numbers */
};

/**
 * Method to validate the credit card expiry date.
 *
 * @param expiryYear - Credit Card expiry date.
 */
export const validateExpiryYear = (expiryYear: string) => {
  /** MM/YYYY pattern regular expression */
  const pattern = /^(0[1-9]|1[0-2])\/(20\d{2})$/;

  if (!pattern.test(expiryYear)) {
    return false; /** Invalid format */
  }

  /** Split the expiry year into month and year parts */
  const [month, year] = expiryYear.split('/');

  /** Convert month and year parts to numbers */
  const numericMonth = parseInt(month, 10);
  const numericYear = parseInt(year, 10);

  /** Check if the month is valid (between 1 and 12) */
  if (numericMonth < 1 || numericMonth > 12) {
    return false; /** Invalid month */
  }

  /** Get the current date */
  const currentDate = new Date();

  /** Get the current year and month */
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1; /** Month is zero-based */

  /** Check if the expiry year is in the future */
  if (numericYear < currentYear || (numericYear === currentYear && numericMonth < currentMonth)) {
    return false; /** Expiry year is in the past */
  }

  return true; /**  Valid expiry year */
};

/**
 * Method to validate the credit card CVV.
 *
 * @param cvv - Credit Card CVV.
 */
export const validateCVV = (cvv: string) => {
  const cvvPattern = /^\d{3,4}$/;

  return cvvPattern.test(cvv);
};

/**
 * Method to validate the zip code.
 *
 * @param zip - Zipcode.
 */
export const validateZipCode = (zip: string) => {
  const zipCodePattern = /^\d{5,6}$/;

  return zipCodePattern.test(zip);
};

/**
 * Method to validate the zip code for payment section.
 *
 * @param zip - Zipcode.
 */
export const validatePaymentZipCode = (zip: string) => {
  // Pattern to allow alphanumeric characters, 5 or 6 characters long
  const zipCodePattern = /^[A-Za-z0-9]{5,6}$/;

  return zipCodePattern.test(zip);
};

/**
 * Function to prevent entering negative numbers and add $ sign to the number.
 *
 * @param event - the input event
 */
export const addDollarSign = (event: ChangeEvent<HTMLInputElement>, addSign = false) => {
  let inputValue = event.target.value;

  /** Remove non-numeric characters and leading zeros */
  inputValue = inputValue.replace(/[^0-9.]/g, '').replace(/^0+/, '');

  /** Ensure the value is not negative */
  if (parseFloat(inputValue) < 0) {
    inputValue = '0';
  }

  /**  Add $ prefix */
  inputValue = addSign ? `$${inputValue || '0'}` : `${inputValue || '0'}`;

  /** Update input value */
  event.target.value = inputValue;
};

/**
 * Method to handle the validation of the input.
 *
 * @param name - The name of the order.
 */
export const validateEmptyValue = (name: string) => {
  if (!name || name.trim().length === 0) {
    return false;
  }

  return true;
};

export const formatToUSCurrency = (value: number | string): string => {
  const numericValue = typeof value === 'string' ? parseFloat(value) : value;

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numericValue);
};

export const parseCurrencyToValue = (formattedValue: string): number => {
  const numericValue = formattedValue.replace(/[^0-9.]/g, '');

  return parseFloat(numericValue) || 0;
};

/**
 * Method to convert the input value to US currency format.
 *
 * @param event - the input event
 */
export const convertInputValueToUsCurrency = (event: React.ChangeEvent<HTMLInputElement>) => {
  const inputElement = event.target;
  const inputValue = inputElement.value;

  if (inputValue === '' || inputValue === '$') {
    inputElement.value = '$';
    return null;
  }

  /** Save the cursor position before modifying the value */
  const cursorPosition = inputElement.selectionStart ?? inputValue.length;

  /** Remove all non-numeric characters except '.' */
  let rawValue = inputValue.replace(/[^0-9.]/g, '');

  /** Ensure only one decimal point is allowed */
  const dotIndex = rawValue.indexOf('.');
  if (dotIndex !== -1) {
    /** Remove any additional decimal points after the first one */
    rawValue = rawValue.slice(0, dotIndex + 1) + rawValue.slice(dotIndex + 1).replace(/\./g, '');
  }

  /** Split into integer and decimal parts */
  const [integerPart, decimalPart] = rawValue.split('.');

  /** Format the integer part with commas */
  const formattedIntegerPart = new Intl.NumberFormat('en-US', {
    style: 'decimal',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(parseInt(integerPart || '0', 10));

  /** Restrict decimal part to 2 places, if it exists */
  const formattedDecimalPart = decimalPart !== undefined ? `.${decimalPart.slice(0, 2)}` : '';

  /** Construct the final formatted value */
  const formattedValue = `$${formattedIntegerPart}${formattedDecimalPart}`;

  /** Update the input field */
  inputElement.value = formattedValue;

  /** Calculate the new cursor position */
  const lengthDifference = formattedValue.length - inputValue.length;
  const newCursorPosition = cursorPosition + lengthDifference;

  /** Restore the cursor position */
  setTimeout(() => {
    inputElement.setSelectionRange(newCursorPosition, newCursorPosition);
  }, 0);

  return null;
};
